import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { UseFormReturn } from "react-hook-form";
import { ToolFormValues } from "@/types";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface BasicInfoSectionProps {
  form: UseFormReturn<ToolFormValues>;
}

export const BasicInfoSection = ({ form }: BasicInfoSectionProps) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('يرجى اختيار ملف صورة صالح');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('حجم الصورة يجب أن لا يتجاوز 5 ميجابايت');
      return;
    }

    try {
      setIsUploading(true);
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError, data } = await supabase.storage
        .from('tool-images')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('tool-images')
        .getPublicUrl(filePath);

      form.setValue('imageUrl', publicUrl);
      toast.success('تم رفع الصورة بنجاح');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('حدث خطأ أثناء رفع الصورة');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-lg font-semibold text-gray-900">اسم الأداة</FormLabel>
            <FormControl>
              <Input 
                {...field} 
                className="text-right text-gray-900 bg-white border-2 border-gray-200 focus:border-primary focus:ring-primary h-12" 
                placeholder="أدخل اسم الأداة..."
              />
            </FormControl>
            <FormMessage className="text-red-500 text-sm" />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-lg font-semibold text-gray-900">وصف الأداة</FormLabel>
            <FormControl>
              <Textarea 
                {...field} 
                className="text-right text-gray-900 bg-white border-2 border-gray-200 focus:border-primary focus:ring-primary min-h-[120px] resize-y" 
                placeholder="اكتب وصفاً مختصراً للأداة..."
              />
            </FormControl>
            <FormMessage className="text-red-500 text-sm" />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="imageUrl"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-lg font-semibold text-gray-900">صورة الأداة</FormLabel>
            <div className="space-y-4">
              <FormControl>
                <Input 
                  {...field} 
                  className="text-right text-gray-900 bg-white border-2 border-gray-200 focus:border-primary focus:ring-primary h-12" 
                  placeholder="أدخل رابط صورة الأداة..."
                  dir="ltr"
                />
              </FormControl>
              <div className="flex items-center gap-4">
                <Button
                  type="button"
                  variant="outline"
                  className="relative"
                  disabled={isUploading}
                >
                  <input
                    type="file"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    onChange={handleFileUpload}
                    accept="image/*"
                  />
                  <Upload className="w-4 h-4 ml-2" />
                  {isUploading ? 'جاري الرفع...' : 'رفع صورة'}
                </Button>
                {field.value && (
                  <img 
                    src={field.value} 
                    alt="معاينة الصورة" 
                    className="w-12 h-12 rounded-full object-cover border border-gray-200"
                  />
                )}
              </div>
            </div>
            <FormMessage className="text-red-500 text-sm" />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="tool_url"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-lg font-semibold text-gray-900">رابط الأداة</FormLabel>
            <FormControl>
              <Input 
                {...field} 
                className="text-right text-gray-900 bg-white border-2 border-gray-200 focus:border-primary focus:ring-primary h-12" 
                placeholder="أدخل رابط الأداة..."
                dir="ltr"
              />
            </FormControl>
            <FormMessage className="text-red-500 text-sm" />
          </FormItem>
        )}
      />
    </div>
  );
};